import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {AnalysisItem, KodataCategoryGroup, PopulationCompare, SalesCompare} from '@/map/@types';
import {useQueries} from '@tanstack/react-query';
import {getAreaQueryVariables} from './variables';

export const useComparePopulationByPoint = (items: AnalysisItem[]) => {
  const request = useGqlRequest({
    query: COMPARE_POPULATION_BY_POINT,
  });

  const queryFn = (
    item: AnalysisItem
  ):
    | undefined
    | Promise<{
        result?: PopulationCompare;
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request(variant);
  };

  const select = (data?: {result?: PopulationCompare}) => data?.result;

  const queries = useQueries({
    queries: items.map((item) => ({
      select,
      queryFn: () => queryFn(item),
      queryKey: ['map', 'area-compare', 'circle', item.id, 'population'],
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
    })),
  });

  const refetchAll = () => {
    queries.forEach((query) => {
      query.refetch();
    });
  };

  return {
    queries,
    refetchAll,
  };
};

export const useComparePopulationByPolygon = (items: AnalysisItem[]) => {
  const request = useGqlRequest({
    query: COMPARE_POPULATION_BY_POLYGON,
  });

  const queryFn = (
    item: AnalysisItem
  ):
    | undefined
    | Promise<{
        result?: PopulationCompare;
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request(variant);
  };

  const select = (data?: {result?: PopulationCompare}) => data?.result;

  const queries = useQueries({
    queries: items.map((item) => ({
      select,
      queryFn: () => queryFn(item),
      queryKey: ['map', 'area-compare', 'polygon', item.id, 'population'],
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
    })),
  });

  const refetchAll = () => {
    queries.forEach((query) => {
      query.refetch();
    });
  };

  return {
    queries,
    refetchAll,
  };
};

export const useCompareSalesByPoint = (
  items: AnalysisItem[],
  category: KodataCategoryGroup
) => {
  const request = useGqlRequest({
    query: COMPARE_SALES_BY_POINT,
  });

  const queryFn = (
    item: AnalysisItem
  ):
    | undefined
    | Promise<{
        result?: SalesCompare;
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request({...variant, categories: [category]});
  };

  const select = (data?: {result?: SalesCompare}) => data?.result;

  const queries = useQueries({
    queries: items.map((item) => ({
      select,
      queryFn: () => queryFn(item),
      queryKey: ['map', 'area-compare', 'circle', item.id, 'sales', category],
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
    })),
  });

  const refetchAll = () => {
    queries.forEach((query) => {
      query.refetch();
    });
  };

  return {
    queries,
    refetchAll,
  };
};

export const useCompareSalesByPolygon = (
  items: AnalysisItem[],
  category: KodataCategoryGroup
) => {
  const request = useGqlRequest({
    query: COMPARE_SALES_BY_POLYGON,
  });

  const queryFn = (
    item: AnalysisItem
  ):
    | undefined
    | Promise<{
        result?: SalesCompare;
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request({...variant, categories: [category]});
  };

  const select = (data?: {result?: SalesCompare}) => data?.result;

  const queries = useQueries({
    queries: items.map((item) => ({
      select,
      queryFn: () => queryFn(item),
      queryKey: ['map', 'area-compare', 'polygon', item.id, 'sales', category],
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
    })),
  });

  const refetchAll = () => {
    queries.forEach((query) => {
      query.refetch();
    });
  };

  return {
    queries,
    refetchAll,
  };
};

const POPULATION_RESULT = `
  floatingPopulation {
    day {
      hour
      totalPopulation
    }
    week {
      hour
      totalPopulation
    }
  }
  house {
    aptCount
    houseCount
    officeCount
    villaCount
  }
  officeWorker {
    female
    male
  }
  population {
    female
    male
  }
  students {
    daycareCenter
    elementary
    high
    kindergarten
    middle
    university
  }
`;

const COMPARE_POPULATION_BY_POINT = gql`
  query areaComparePopulationByPoint($lat: Float!, $lng: Float!, $radius: Float!) {
    result: areaComparePopulationByPoint(lat: $lat, lng: $lng, radius: $radius) {
      ${POPULATION_RESULT}
    }
  }
`;

const COMPARE_POPULATION_BY_POLYGON = gql`
  query areaComparePopulationByPolygon($polygon: GeoPolygonArgs!) {
    result: areaComparePopulationByPolygon(polygon: $polygon) {
      ${POPULATION_RESULT}
    }
  }
`;

const SALES_RESULT = gql`
  dayOfWeek
  monthlyStoreCount {
    baseDate
    monthlyStoreCount {
      count
      date
    }
    type
  }
  salesSummary {
    avgEstSales
    cardSalesAvg
    totalEstSales
  }
  storeCount {
    activeStoreCount
    storeCount
  }
  time
  ageGender {
    femaleAgeGender
    maleAgeGender
  }
`;

const COMPARE_SALES_BY_POINT = gql`
  query areaCompareSalesByPoint($categories: [Category!]!, $lat: Float!, $lng: Float!, $radius: Float!) {
    result: areaCompareSalesByPoint(categories: $categories, lat: $lat, lng: $lng, radius: $radius) {
      ${SALES_RESULT}
    }
  }
`;

const COMPARE_SALES_BY_POLYGON = gql`
  query areaCompareSalesByPolygon($categories: [Category!]!, $polygon: GeoPolygonArgs!) {
    result: areaCompareSalesByPolygon(categories: $categories, polygon: $polygon) {
      ${SALES_RESULT}
    }
  }
`;
